import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import './ProviderPortal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
const SESSION_MAX_MS = 8 * 60 * 60 * 1000;
const LOGIN_TS_KEY = 'iu_login_at';

function getUserPool() {
  if (!POOL_ID || !CLIENT_ID) return null;
  return new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID });
}

// ═══════════════════════════════════════════════════════════════════
// PIPELINE STEPS
// ═══════════════════════════════════════════════════════════════════
const PIPELINE_STEPS = [
  { key: 'checking_eligibility', label: 'Eligibility', icon: '🔍' },
  { key: 'checking_benefits', label: 'Benefits', icon: '📋' },
  { key: 'checking_necessity', label: 'Necessity', icon: '🩺' },
  { key: 'checking_coding', label: 'Coding', icon: '🔢' },
  { key: 'checking_tariff', label: 'Tariff', icon: '💰' },
  { key: 'deciding', label: 'Decision', icon: '⚖️' },
];

const STATUS_COLOR = {
  pending: '#94a3b8',
  checking_eligibility: '#3b82f6',
  checking_benefits: '#6366f1',
  checking_necessity: '#8b5cf6',
  checking_coding: '#a855f7',
  checking_tariff: '#d946ef',
  deciding: '#ec4899',
  approved: '#059669',
  denied: '#dc2626',
  routed_to_human: '#d97706',
  info_requested: '#0891b2',
  error: '#dc2626',
};

// Common CPT codes for the dropdown
const CPT_CODES = [
  { code: '99213', desc: 'Office visit - established, moderate' },
  { code: '99214', desc: 'Office visit - established, high' },
  { code: '99283', desc: 'Emergency dept visit - moderate' },
  { code: '99284', desc: 'Emergency dept visit - high' },
  { code: '99285', desc: 'Emergency dept visit - critical' },
  { code: '70553', desc: 'MRI brain with/without contrast' },
  { code: '71260', desc: 'CT chest with contrast' },
  { code: '74177', desc: 'CT abdomen/pelvis with contrast' },
  { code: '43239', desc: 'Upper GI endoscopy with biopsy' },
  { code: '27447', desc: 'Total knee replacement' },
  { code: '33533', desc: 'Coronary artery bypass (CABG)' },
  { code: '59510', desc: 'Cesarean delivery' },
  { code: '59400', desc: 'Routine obstetric care + delivery' },
  { code: '97110', desc: 'Therapeutic exercises' },
  { code: '90837', desc: 'Psychotherapy 60 min' },
  { code: '96413', desc: 'Chemotherapy infusion' },
  { code: '36561', desc: 'Central venous catheter insertion' },
  { code: '50590', desc: 'Lithotripsy (kidney stones)' },
  { code: '99291', desc: 'Critical care first 30-74 min' },
  { code: 'OTHER', desc: 'Other (specify below)' },
];

// Common ICD-10 codes
const ICD10_CODES = [
  { code: 'E11.65', desc: 'Type 2 diabetes with hyperglycemia' },
  { code: 'E13.10', desc: 'Other diabetes with ketoacidosis' },
  { code: 'I10', desc: 'Essential hypertension' },
  { code: 'I25.10', desc: 'Coronary artery disease' },
  { code: 'J18.9', desc: 'Pneumonia, unspecified' },
  { code: 'K80.20', desc: 'Gallbladder calculus without obstruction' },
  { code: 'M54.5', desc: 'Low back pain' },
  { code: 'N20.0', desc: 'Calculus of kidney (kidney stone)' },
  { code: 'C34.90', desc: 'Lung cancer, unspecified' },
  { code: 'C50.919', desc: 'Breast cancer, unspecified' },
  { code: 'G43.909', desc: 'Migraine, unspecified' },
  { code: 'J45.20', desc: 'Mild intermittent asthma' },
  { code: 'K21.0', desc: 'GERD with esophagitis' },
  { code: 'N18.3', desc: 'Chronic kidney disease stage 3' },
  { code: 'O80', desc: 'Single spontaneous delivery' },
  { code: 'R07.9', desc: 'Chest pain, unspecified' },
  { code: 'S72.001A', desc: 'Fracture of femur' },
  { code: 'Z41.1', desc: 'Cosmetic procedure (excluded)' },
  { code: 'OTHER', desc: 'Other (specify below)' },
];


// ═══════════════════════════════════════════════════════════════════
// MAIN PROVIDER PORTAL COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function ProviderPortal({ onAuthChange }) {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('login');
  const authChecked = useRef(false);

  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;
    const pool = getUserPool();
    if (!pool) return;
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) return;
    const loginTs = localStorage.getItem(LOGIN_TS_KEY);
    if (loginTs && Date.now() - Number(loginTs) > SESSION_MAX_MS) {
      cognitoUser.signOut();
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) return;
      const payload = session.getIdToken().payload;
      const groups = payload['cognito:groups'] || [];
      cognitoUser.getUserAttributes((attrErr, attrs) => {
        if (attrErr) return;
        const emailAttr = attrs.find(a => a.Name === 'email');
        const nameAttr = attrs.find(a => a.Name === 'name') || attrs.find(a => a.Name === 'custom:provider_name');
        const u = {
          email: emailAttr?.Value || '',
          name: nameAttr?.Value || emailAttr?.Value || '',
          providerId: attrs.find(a => a.Name === 'custom:provider_id')?.Value || 'PRV-0001',
          providerName: attrs.find(a => a.Name === 'custom:provider_name')?.Value || '',
          license: attrs.find(a => a.Name === 'custom:license')?.Value || '',
          groups,
        };
        setUser(u);
        setView('dashboard');
        if (onAuthChange) onAuthChange(u);
      });
    });
  }, [onAuthChange]);

  const handleLogin = (u) => {
    setUser(u);
    setView('dashboard');
    localStorage.setItem(LOGIN_TS_KEY, String(Date.now()));
    if (onAuthChange) onAuthChange(u);
  };

  if (!user) {
    return <ProviderLogin onLogin={handleLogin} />;
  }

  return (
    <div className="provider-portal">
      <ProviderNav user={user} view={view} setView={setView} />
      <div className="provider-content">
        {view === 'dashboard' && <ProviderDashboard user={user} setView={setView} />}
        {view === 'submit' && <SubmitPreAuth user={user} onSubmitted={() => setView('requests')} />}
        {view === 'submit-claim' && <SubmitClaim user={user} onSubmitted={() => setView('my-claims')} />}
        {view === 'requests' && <MyRequests user={user} />}
        {view === 'my-claims' && <MyClaims user={user} />}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════════
function ProviderLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const pool = getUserPool();
    if (!pool) { setError('Auth not configured'); setLoading(false); return; }

    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        const payload = session.getIdToken().payload;
        const groups = payload['cognito:groups'] || [];
        cognitoUser.getUserAttributes((err, attrs) => {
          setLoading(false);
          if (err) { setError('Failed to get user data'); return; }
          const emailAttr = attrs.find(a => a.Name === 'email');
          const u = {
            email: emailAttr?.Value || email,
            name: attrs.find(a => a.Name === 'name')?.Value || email,
            providerId: attrs.find(a => a.Name === 'custom:provider_id')?.Value || 'PRV-0001',
            providerName: attrs.find(a => a.Name === 'custom:provider_name')?.Value || '',
            license: attrs.find(a => a.Name === 'custom:license')?.Value || '',
            groups,
          };
          onLogin(u);
        });
      },
      onFailure: (err) => {
        setLoading(false);
        setError(err.message || 'Login failed');
      },
      newPasswordRequired: () => {
        setLoading(false);
        setError('Password change required. Contact admin.');
      },
    });
  };

  return (
    <div className="provider-login-wrap">
      <div className="provider-login-card">
        <div className="provider-login-header">
          <div className="provider-login-icon">🩺</div>
          <h1>NebulaSure Provider</h1>
          <p>Pre-Authorization Portal for Healthcare Providers</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="provider-error">{error}</div>}
          <div className="provider-field">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div className="provider-field">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button type="submit" disabled={loading} className="provider-btn-primary">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// NAVIGATION
// ═══════════════════════════════════════════════════════════════════
function ProviderNav({ user, view, setView }) {
  return (
    <div className="provider-nav">
      <div className="provider-nav-brand">
        <span className="provider-nav-icon">🩺</span>
        <span className="provider-nav-title">NebulaSure Provider</span>
      </div>
      <div className="provider-nav-tabs">
        <button className={`provider-nav-tab ${view === 'dashboard' ? 'active' : ''}`} onClick={() => setView('dashboard')}>Dashboard</button>
        <button className={`provider-nav-tab ${view === 'submit' ? 'active' : ''}`} onClick={() => setView('submit')}>Submit Pre-Auth</button>
        <button className={`provider-nav-tab ${view === 'submit-claim' ? 'active' : ''}`} onClick={() => setView('submit-claim')}>Submit Claim</button>
        <button className={`provider-nav-tab ${view === 'requests' ? 'active' : ''}`} onClick={() => setView('requests')}>My Pre-Auths</button>
        <button className={`provider-nav-tab ${view === 'my-claims' ? 'active' : ''}`} onClick={() => setView('my-claims')}>My Claims</button>
      </div>
      <div className="provider-nav-user">
        <span>{user.name}</span>
        <span className="provider-nav-pid">{user.providerId}</span>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════
function ProviderDashboard({ user, setView }) {
  const [stats, setStats] = useState({ total: 0, approved: 0, denied: 0, pending: 0, routed: 0 });
  const [claimStats, setClaimStats] = useState({ total: 0, approved: 0, processing: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/preauth?provider_id=${encodeURIComponent(user.providerId)}`).then(r => r.json()),
      fetch(`${API_URL}/claims?provider_id=${encodeURIComponent(user.providerId)}`).then(r => r.json()),
    ])
      .then(([preauthData, claimsData]) => {
        const preauths = preauthData.preauths || [];
        setStats({
          total: preauths.length,
          approved: preauths.filter(p => p.status === 'approved').length,
          denied: preauths.filter(p => p.status === 'denied').length,
          pending: preauths.filter(p => !['approved', 'denied', 'routed_to_human', 'info_requested', 'error'].includes(p.status)).length,
          routed: preauths.filter(p => p.status === 'routed_to_human').length,
        });
        const claims = claimsData.claims || [];
        setClaimStats({
          total: claims.length,
          approved: claims.filter(c => c.status === 'approved').length,
          processing: claims.filter(c => !['approved', 'denied'].includes(c.status)).length,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.providerId]);

  return (
    <div className="provider-dashboard">
      <div className="provider-dashboard-header">
        <h2>Provider Dashboard</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="provider-btn-primary" onClick={() => setView('submit')}>+ New Pre-Auth</button>
          <button className="provider-btn-primary" onClick={() => setView('submit-claim')} style={{ background: '#059669' }}>+ Submit Claim</button>
        </div>
      </div>

      {loading ? (
        <div className="provider-loading">Loading...</div>
      ) : (
        <>
          <h3 style={{ margin: '20px 0 12px', color: '#374151' }}>Pre-Authorizations</h3>
          <div className="provider-stats-grid">
            <div className="provider-stat-card">
              <div className="provider-stat-value">{stats.total}</div>
              <div className="provider-stat-label">Total Requests</div>
            </div>
            <div className="provider-stat-card stat-success">
              <div className="provider-stat-value">{stats.approved}</div>
              <div className="provider-stat-label">Approved</div>
            </div>
            <div className="provider-stat-card stat-danger">
              <div className="provider-stat-value">{stats.denied}</div>
              <div className="provider-stat-label">Denied</div>
            </div>
            <div className="provider-stat-card stat-warning">
              <div className="provider-stat-value">{stats.pending}</div>
              <div className="provider-stat-label">In Progress</div>
            </div>
            <div className="provider-stat-card stat-info">
              <div className="provider-stat-value">{stats.routed}</div>
              <div className="provider-stat-label">Under Review</div>
            </div>
          </div>

          <h3 style={{ margin: '28px 0 12px', color: '#374151' }}>Claims (Direct Billing)</h3>
          <div className="provider-stats-grid">
            <div className="provider-stat-card">
              <div className="provider-stat-value">{claimStats.total}</div>
              <div className="provider-stat-label">Total Claims Filed</div>
            </div>
            <div className="provider-stat-card stat-success">
              <div className="provider-stat-value">{claimStats.approved}</div>
              <div className="provider-stat-label">Paid</div>
            </div>
            <div className="provider-stat-card stat-warning">
              <div className="provider-stat-value">{claimStats.processing}</div>
              <div className="provider-stat-label">Processing</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SUBMIT PRE-AUTH FORM
// ═══════════════════════════════════════════════════════════════════
function SubmitPreAuth({ user, onSubmitted }) {
  const [form, setForm] = useState({
    applicant_id: '',
    member_name: '',
    member_dob: '',
    cpt_code: '',
    cpt_custom: '',
    service_description: '',
    service_quantity: 1,
    icd10_code: '',
    icd10_custom: '',
    diagnosis_description: '',
    clinical_notes: '',
    requested_amount: '',
    urgency: 'routine',
  });
  const [docFile, setDocFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      // Prepare supporting document
      let supporting_document = null;
      if (docFile) {
        const bytes = new Uint8Array(await docFile.arrayBuffer());
        let binary = '';
        const chunkSize = 8192;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.slice(i, i + chunkSize));
        }
        supporting_document = btoa(binary);
      }

      const cptCode = form.cpt_code === 'OTHER' ? form.cpt_custom : form.cpt_code;
      const icd10Code = form.icd10_code === 'OTHER' ? form.icd10_custom : form.icd10_code;

      const payload = {
        provider_id: user.providerId,
        provider_name: user.providerName || user.name,
        provider_license: user.license || '',
        applicant_id: form.applicant_id,
        member_name: form.member_name,
        member_dob: form.member_dob,
        requested_service: {
          cpt_code: cptCode,
          description: form.service_description || CPT_CODES.find(c => c.code === form.cpt_code)?.desc || cptCode,
          quantity: parseInt(form.service_quantity) || 1,
        },
        diagnosis: {
          icd10_code: icd10Code,
          description: form.diagnosis_description || ICD10_CODES.find(c => c.code === form.icd10_code)?.desc || icd10Code,
          clinical_notes: form.clinical_notes,
        },
        requested_amount: parseFloat(form.requested_amount) || 0,
        urgency: form.urgency,
        supporting_document,
      };

      const resp = await fetch(`${API_URL}/submit-preauth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await resp.json();
      if (!resp.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      setSuccess(`Pre-auth ${result.preauth_id} submitted! AI pipeline started.`);
      setTimeout(() => onSubmitted(), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="provider-submit">
      <h2>Submit Pre-Authorization Request</h2>
      <form className="provider-form" onSubmit={handleSubmit}>
        {error && <div className="provider-error">{error}</div>}
        {success && <div className="provider-success">{success}</div>}

        {/* Member Info */}
        <fieldset className="provider-fieldset">
          <legend>Member Information</legend>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>Member ID (APP-XXXX) *</label>
              <input type="text" value={form.applicant_id} onChange={e => handleChange('applicant_id', e.target.value)} placeholder="APP-0008" required pattern="APP-\d{4}" />
            </div>
            <div className="provider-field">
              <label>Member Name *</label>
              <input type="text" value={form.member_name} onChange={e => handleChange('member_name', e.target.value)} placeholder="Elie NAddaf" required />
            </div>
            <div className="provider-field">
              <label>Date of Birth</label>
              <input type="date" value={form.member_dob} onChange={e => handleChange('member_dob', e.target.value)} />
            </div>
          </div>
        </fieldset>

        {/* Service Request */}
        <fieldset className="provider-fieldset">
          <legend>Requested Service</legend>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>CPT Code *</label>
              <select value={form.cpt_code} onChange={e => handleChange('cpt_code', e.target.value)} required>
                <option value="">Select CPT code...</option>
                {CPT_CODES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.desc}</option>)}
              </select>
            </div>
            {form.cpt_code === 'OTHER' && (
              <div className="provider-field">
                <label>Custom CPT Code</label>
                <input type="text" value={form.cpt_custom} onChange={e => handleChange('cpt_custom', e.target.value)} placeholder="e.g. 99999" />
              </div>
            )}
            <div className="provider-field">
              <label>Quantity</label>
              <input type="number" min="1" value={form.service_quantity} onChange={e => handleChange('service_quantity', e.target.value)} />
            </div>
          </div>
          <div className="provider-field">
            <label>Service Description</label>
            <input type="text" value={form.service_description} onChange={e => handleChange('service_description', e.target.value)} placeholder="e.g. Emergency room visit for diabetic ketoacidosis" />
          </div>
        </fieldset>

        {/* Diagnosis */}
        <fieldset className="provider-fieldset">
          <legend>Diagnosis</legend>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>ICD-10 Code *</label>
              <select value={form.icd10_code} onChange={e => handleChange('icd10_code', e.target.value)} required>
                <option value="">Select ICD-10 code...</option>
                {ICD10_CODES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.desc}</option>)}
              </select>
            </div>
            {form.icd10_code === 'OTHER' && (
              <div className="provider-field">
                <label>Custom ICD-10 Code</label>
                <input type="text" value={form.icd10_custom} onChange={e => handleChange('icd10_custom', e.target.value)} placeholder="e.g. E11.65" />
              </div>
            )}
          </div>
          <div className="provider-field">
            <label>Diagnosis Description</label>
            <input type="text" value={form.diagnosis_description} onChange={e => handleChange('diagnosis_description', e.target.value)} placeholder="e.g. Type 2 diabetes with DKA episode" />
          </div>
          <div className="provider-field">
            <label>Clinical Notes *</label>
            <textarea value={form.clinical_notes} onChange={e => handleChange('clinical_notes', e.target.value)} rows={4} placeholder="Patient presented with... history of... examination findings..." required />
          </div>
        </fieldset>

        {/* Amount & Urgency */}
        <fieldset className="provider-fieldset">
          <legend>Amount & Urgency</legend>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>Requested Amount (USD) *</label>
              <input type="number" min="0" step="0.01" value={form.requested_amount} onChange={e => handleChange('requested_amount', e.target.value)} placeholder="4200.00" required />
            </div>
            <div className="provider-field">
              <label>Urgency *</label>
              <select value={form.urgency} onChange={e => handleChange('urgency', e.target.value)} required>
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
          </div>
        </fieldset>

        {/* Supporting Document */}
        <fieldset className="provider-fieldset">
          <legend>Supporting Document (Optional)</legend>
          <div className="provider-field">
            <label>Upload PDF</label>
            <input type="file" accept=".pdf" onChange={e => setDocFile(e.target.files[0] || null)} />
          </div>
        </fieldset>

        <button type="submit" className="provider-btn-primary provider-btn-lg" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Pre-Authorization Request'}
        </button>
      </form>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SUBMIT CLAIM (Provider Direct Billing)
// ═══════════════════════════════════════════════════════════════════
const CLAIM_TYPES = [
  'Medical/Hospitalization',
  'Outpatient/Ambulatory',
  'Prescription Drugs',
  "Doctor's Visit",
];

function SubmitClaim({ user, onSubmitted }) {
  const [form, setForm] = useState({
    applicant_id: '',
    claim_type: '',
    claim_amount: '',
    incident_date: '',
    description: '',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setSubmitting(true);

    try {
      let claim_document = null;
      if (pdfFile) {
        const buffer = await pdfFile.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        let binary = '';
        const chunkSize = 8192;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.slice(i, i + chunkSize));
        }
        claim_document = btoa(binary);
      }

      const payload = {
        applicant_id: form.applicant_id.trim(),
        claim_type: form.claim_type,
        claim_amount: parseFloat(form.claim_amount),
        incident_date: form.incident_date,
        description: form.description,
        provider_name: user.providerName || user.name,
        submitted_by: 'provider',
        provider_id: user.providerId,
        provider_license: user.license || '',
      };
      if (claim_document) payload.claim_document = claim_document;

      const resp = await fetch(`${API_URL}/submit-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || 'Submission failed');
      } else {
        setResult(data);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="provider-submit-preauth">
        <div className="provider-success-card">
          <div className="provider-success-icon">✅</div>
          <h2>Claim Submitted Successfully</h2>
          <p>Claim ID: <strong>{result.claim_id}</strong></p>
          <p>Member: <strong>{form.applicant_id}</strong></p>
          <p>Amount: <strong>${parseFloat(form.claim_amount).toLocaleString()}</strong></p>
          <p style={{ marginTop: '12px', color: '#6b7280' }}>
            The FNOL pipeline will analyze this claim automatically. You can track its progress in "My Claims".
          </p>
          <button className="provider-btn-primary" style={{ marginTop: '20px' }} onClick={onSubmitted}>
            View My Claims
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-submit-preauth">
      <h2>Submit Claim — Direct Billing</h2>
      <p className="provider-form-subtitle">File a claim on behalf of a member after treatment (direct billing). The FNOL pipeline will process it automatically.</p>

      <form onSubmit={handleSubmit} className="provider-form">
        {error && <div className="provider-error">{error}</div>}

        <fieldset className="provider-fieldset">
          <legend>Member Information</legend>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>Member ID *</label>
              <input
                type="text"
                placeholder="APP-0004"
                value={form.applicant_id}
                onChange={e => handleChange('applicant_id', e.target.value)}
                pattern="^APP-\d{4}$"
                title="Format: APP-XXXX"
                required
              />
              <small>The member's NebulaSure ID (e.g., APP-0004)</small>
            </div>
          </div>
        </fieldset>

        <fieldset className="provider-fieldset">
          <legend>Claim Details</legend>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>Claim Type *</label>
              <select value={form.claim_type} onChange={e => handleChange('claim_type', e.target.value)} required>
                <option value="">Select type...</option>
                {CLAIM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="provider-field">
              <label>Amount (USD) *</label>
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="1500.00"
                value={form.claim_amount}
                onChange={e => handleChange('claim_amount', e.target.value)}
                required
              />
            </div>
          </div>
          <div className="provider-form-row">
            <div className="provider-field">
              <label>Service Date *</label>
              <input
                type="date"
                value={form.incident_date}
                onChange={e => handleChange('incident_date', e.target.value)}
                required
              />
            </div>
          </div>
          <div className="provider-field">
            <label>Description / Clinical Notes</label>
            <textarea
              rows={4}
              placeholder="Diagnosis, treatment performed, relevant clinical details..."
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
            />
          </div>
        </fieldset>

        <fieldset className="provider-fieldset">
          <legend>Supporting Document</legend>
          <div className="provider-field">
            <label>Invoice / Medical Report (PDF)</label>
            <input type="file" accept=".pdf" onChange={handleFileChange} />
            {pdfFile && <small>📄 {pdfFile.name} ({(pdfFile.size / 1024).toFixed(1)} KB)</small>}
          </div>
        </fieldset>

        <button type="submit" className="provider-btn-primary provider-btn-lg" disabled={submitting}>
          {submitting ? 'Submitting Claim...' : 'Submit Claim (Direct Billing)'}
        </button>
      </form>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// MY CLAIMS (Provider's submitted claims)
// ═══════════════════════════════════════════════════════════════════
function MyClaims({ user }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClaims = useCallback(() => {
    fetch(`${API_URL}/claims?provider_id=${encodeURIComponent(user.providerId)}`)
      .then(r => r.json())
      .then(data => setClaims(data.claims || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.providerId]);

  useEffect(() => {
    fetchClaims();
    const interval = setInterval(fetchClaims, 10000);
    return () => clearInterval(interval);
  }, [fetchClaims]);

  const getStatusColor = (status) => {
    const map = { submitted: '#3b82f6', processing: '#6366f1', approved: '#059669', denied: '#dc2626', pending_info: '#d97706', fraud_review: '#dc2626' };
    return map[status] || '#94a3b8';
  };

  return (
    <div className="provider-requests">
      <h2>My Submitted Claims (Direct Billing)</h2>
      {loading ? (
        <div className="provider-loading">Loading claims...</div>
      ) : claims.length === 0 ? (
        <div className="provider-empty">
          <p>No claims submitted yet.</p>
          <p style={{ color: '#6b7280', fontSize: '0.9em', marginTop: '8px' }}>Claims you submit on behalf of members will appear here.</p>
        </div>
      ) : (
        <div className="provider-requests-list">
          {claims.map(claim => (
            <div key={claim.claim_id} className="provider-request-card">
              <div className="provider-request-header">
                <span className="provider-request-id">{claim.claim_id}</span>
                <span className="provider-status-badge" style={{ background: getStatusColor(claim.status) }}>
                  {claim.status?.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="provider-request-body">
                <div><strong>Member:</strong> {claim.applicant_name || claim.applicant_id}</div>
                <div><strong>Type:</strong> {claim.claim_type}</div>
                <div><strong>Amount:</strong> ${Number(claim.claim_amount || 0).toLocaleString()}</div>
                <div><strong>Date:</strong> {claim.incident_date}</div>
                {claim.fnol_route && <div><strong>Route:</strong> {claim.fnol_route?.replace(/_/g, ' ')}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// MY REQUESTS (with pipeline visualization)
// ═══════════════════════════════════════════════════════════════════
function MyRequests({ user }) {
  const [preauths, setPreauths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchPreauths = useCallback(() => {
    fetch(`${API_URL}/preauth?provider_id=${encodeURIComponent(user.providerId)}`)
      .then(r => r.json())
      .then(data => setPreauths(data.preauths || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.providerId]);

  useEffect(() => {
    fetchPreauths();
    const interval = setInterval(fetchPreauths, 8000);
    return () => clearInterval(interval);
  }, [fetchPreauths]);

  if (selected) {
    return <PreAuthDetail preauth={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="provider-requests">
      <h2>My Pre-Authorization Requests</h2>
      {loading ? (
        <div className="provider-loading">Loading...</div>
      ) : preauths.length === 0 ? (
        <div className="provider-empty">No requests yet. Submit your first pre-authorization.</div>
      ) : (
        <div className="provider-requests-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Member</th>
                <th>Service</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Pipeline</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {preauths.map(p => (
                <tr key={p.preauth_id} onClick={() => setSelected(p)} className="provider-row-clickable">
                  <td className="provider-cell-id">{p.preauth_id}</td>
                  <td>{p.member_name}</td>
                  <td>{p.requested_service?.description?.slice(0, 40) || p.requested_service?.cpt_code || '—'}</td>
                  <td>${Number(p.requested_amount || 0).toLocaleString()}</td>
                  <td>
                    <span className="provider-status-badge" style={{ background: STATUS_COLOR[p.status] || '#94a3b8' }}>
                      {p.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td><MiniPipeline status={p.status} /></td>
                  <td>{p.submitted_at?.slice(0, 10) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// PIPELINE VISUALIZATION (mini)
// ═══════════════════════════════════════════════════════════════════
function MiniPipeline({ status }) {
  const stepIndex = PIPELINE_STEPS.findIndex(s => s.key === status);
  const isComplete = ['approved', 'denied', 'routed_to_human', 'info_requested'].includes(status);

  return (
    <div className="provider-mini-pipeline">
      {PIPELINE_STEPS.map((step, i) => {
        let cls = 'step-pending';
        if (isComplete) cls = 'step-done';
        else if (i < stepIndex) cls = 'step-done';
        else if (i === stepIndex) cls = 'step-active';
        return <div key={step.key} className={`mini-step ${cls}`} title={step.label} />;
      })}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// PRE-AUTH DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════
function PreAuthDetail({ preauth: initialPreauth, onBack }) {
  const [preauth, setPreauth] = useState(initialPreauth);
  const [polling, setPolling] = useState(!['approved', 'denied', 'routed_to_human', 'info_requested', 'error'].includes(initialPreauth.status));

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(() => {
      fetch(`${API_URL}/preauth/${preauth.preauth_id}`)
        .then(r => r.json())
        .then(data => {
          if (data.preauth_id) {
            setPreauth(data);
            if (['approved', 'denied', 'routed_to_human', 'info_requested', 'error'].includes(data.status)) {
              setPolling(false);
            }
          }
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, preauth.preauth_id]);

  const stepIndex = PIPELINE_STEPS.findIndex(s => s.key === preauth.status);
  const isComplete = ['approved', 'denied', 'routed_to_human', 'info_requested', 'error'].includes(preauth.status);

  return (
    <div className="provider-detail">
      <button className="provider-back-btn" onClick={onBack}>← Back to list</button>

      <div className="provider-detail-header">
        <h2>{preauth.preauth_id}</h2>
        <span className="provider-status-badge provider-status-badge-lg" style={{ background: STATUS_COLOR[preauth.status] || '#94a3b8' }}>
          {preauth.status?.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Full Pipeline Visualization */}
      <div className="provider-pipeline-full">
        {PIPELINE_STEPS.map((step, i) => {
          let cls = 'pipeline-pending';
          if (isComplete) cls = 'pipeline-done';
          else if (i < stepIndex) cls = 'pipeline-done';
          else if (i === stepIndex) cls = 'pipeline-active';
          return (
            <div key={step.key} className={`pipeline-step-full ${cls}`}>
              <div className="pipeline-dot" />
              <div className="pipeline-step-label">
                <span className="pipeline-step-icon">{step.icon}</span>
                <span>{step.label}</span>
              </div>
            </div>
          );
        })}
        <div className={`pipeline-step-full ${isComplete ? 'pipeline-done' : 'pipeline-pending'}`}>
          <div className="pipeline-dot" />
          <div className="pipeline-step-label">
            <span className="pipeline-step-icon">✓</span>
            <span>Complete</span>
          </div>
        </div>
      </div>

      {/* Request Details */}
      <div className="provider-detail-grid">
        <div className="provider-detail-section">
          <h3>Request Details</h3>
          <div className="provider-detail-fields">
            <div><strong>Member:</strong> {preauth.member_name} ({preauth.applicant_id})</div>
            <div><strong>Provider:</strong> {preauth.provider_name}</div>
            <div><strong>Service:</strong> {preauth.requested_service?.description || preauth.requested_service?.cpt_code}</div>
            <div><strong>CPT:</strong> {preauth.requested_service?.cpt_code}</div>
            <div><strong>Diagnosis:</strong> {preauth.diagnosis?.description || preauth.diagnosis?.icd10_code}</div>
            <div><strong>ICD-10:</strong> {preauth.diagnosis?.icd10_code}</div>
            <div><strong>Amount:</strong> ${Number(preauth.requested_amount || 0).toLocaleString()}</div>
            <div><strong>Urgency:</strong> {preauth.urgency}</div>
            <div><strong>Submitted:</strong> {preauth.submitted_at?.replace('T', ' ').slice(0, 19)}</div>
          </div>
        </div>

        {/* AI Results */}
        {isComplete && (
          <div className="provider-detail-section">
            <h3>AI Decision</h3>
            <div className="provider-detail-fields">
              <div><strong>Recommendation:</strong> <span className="provider-status-badge" style={{ background: STATUS_COLOR[preauth.ai_recommendation] || '#94a3b8' }}>{preauth.ai_recommendation || '—'}</span></div>
              {preauth.estimated_approved_amount > 0 && (
                <div><strong>Approved Amount:</strong> ${Number(preauth.estimated_approved_amount).toLocaleString()}</div>
              )}
              {preauth.decision_by && <div><strong>Decided by:</strong> {preauth.decision_by}</div>}
              {preauth.decided_at && <div><strong>Decided at:</strong> {preauth.decided_at?.replace('T', ' ').slice(0, 19)}</div>}
            </div>
            {preauth.ai_recommendation_text && (
              <div className="provider-ai-text">
                <h4>AI Analysis</h4>
                <pre>{typeof preauth.ai_recommendation_text === 'string' ? preauth.ai_recommendation_text : JSON.stringify(preauth.ai_recommendation_text, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        {/* Pipeline Step Results */}
        {(preauth.ai_eligibility_result || preauth.ai_benefit_result || preauth.ai_medical_necessity_result) && (
          <div className="provider-detail-section provider-detail-full-width">
            <h3>Pipeline Step Results</h3>
            {preauth.ai_eligibility_result && (
              <details className="provider-step-detail">
                <summary>🔍 Eligibility Check</summary>
                <pre>{typeof preauth.ai_eligibility_result === 'string' ? preauth.ai_eligibility_result : JSON.stringify(preauth.ai_eligibility_result, null, 2)}</pre>
              </details>
            )}
            {preauth.ai_benefit_result && (
              <details className="provider-step-detail">
                <summary>📋 Benefit Coverage</summary>
                <pre>{typeof preauth.ai_benefit_result === 'string' ? preauth.ai_benefit_result : JSON.stringify(preauth.ai_benefit_result, null, 2)}</pre>
              </details>
            )}
            {preauth.ai_medical_necessity_result && (
              <details className="provider-step-detail">
                <summary>🩺 Medical Necessity</summary>
                <pre>{typeof preauth.ai_medical_necessity_result === 'string' ? preauth.ai_medical_necessity_result : JSON.stringify(preauth.ai_medical_necessity_result, null, 2)}</pre>
              </details>
            )}
            {preauth.ai_coding_result && (
              <details className="provider-step-detail">
                <summary>🔢 Coding Coherence</summary>
                <pre>{typeof preauth.ai_coding_result === 'string' ? preauth.ai_coding_result : JSON.stringify(preauth.ai_coding_result, null, 2)}</pre>
              </details>
            )}
            {preauth.ai_tariff_result && (
              <details className="provider-step-detail">
                <summary>💰 Tariff Validation</summary>
                <pre>{typeof preauth.ai_tariff_result === 'string' ? preauth.ai_tariff_result : JSON.stringify(preauth.ai_tariff_result, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
